import { useState, useCallback, useMemo } from 'react';
import { Autocomplete, Icon } from '@shopify/polaris';
import { SearchIcon } from '@shopify/polaris-icons';

export function FieldSelector({ options, value, onChange, placeholder = 'Search fields...' }) {
  const [inputValue, setInputValue] = useState('');
  const [selectedOptions, setSelectedOptions] = useState([value]);

  // Filter options based on search input
  const filteredOptions = useMemo(() => {
    if (!inputValue) {
      return options;
    }

    const searchLower = inputValue.toLowerCase();
    const filtered = [];
    let currentGroup = null;

    options.forEach(option => {
      // Check if it's a group header
      if (option.value?.startsWith('__group_')) {
        currentGroup = option;
        return;
      }

      // Check if option matches search
      const labelMatch = option.label?.toLowerCase().includes(searchLower);
      const valueMatch = option.value?.toLowerCase().includes(searchLower);
      const descMatch = option.description?.toLowerCase().includes(searchLower);

      if (labelMatch || valueMatch || descMatch) {
        // Add group header if not already added
        if (currentGroup && !filtered.includes(currentGroup)) {
          filtered.push(currentGroup);
        }
        filtered.push(option);
      }
    });

    return filtered;
  }, [options, inputValue]);

  const updateText = useCallback((value) => {
    setInputValue(value);
  }, []);

  const updateSelection = useCallback((selected) => {
    const selectedValue = selected[0];
    
    // Don't allow selecting group headers
    if (selectedValue?.startsWith('__group_')) {
      return;
    }

    setSelectedOptions(selected);
    setInputValue('');
    
    if (onChange) {
      onChange(selectedValue);
    }
  }, [onChange]);

  // Get the label for the selected value
  const selectedLabel = useMemo(() => {
    const selectedOption = options.find(opt => opt.value === value);
    return selectedOption?.label || '';
  }, [options, value]);

  const textField = (
    <Autocomplete.TextField
      onChange={updateText}
      label=""
      labelHidden
      value={inputValue}
      placeholder={selectedLabel || placeholder}
      prefix={<Icon source={SearchIcon} />}
      autoComplete="off"
    />
  );

  return (
    <Autocomplete
      options={filteredOptions}
      selected={selectedOptions}
      onSelect={updateSelection}
      textField={textField}
      preferredPosition="below"
    />
  );
}
